import { useCallback, useEffect, useMemo, useState } from 'react';

import { Close } from '@mui/icons-material';
import { CircularProgress, DialogContent, DialogTitle, IconButton } from '@mui/material';

import { useEventBus } from '../../../../hooks/use-event-bus';

import { useConfig, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { QRCodeSVG } from 'qrcode.react';
import { ContactApi } from '../../../../services/api-client/contact-api';
import FormsService from '../../../../services/forms-service/forms-service';
import { PatientDto, PatientInviteMethod } from '../../../../services/forms-service/forms-service-types';
import { checkPatientData } from './helpers';
import { CenterContent, Dialog } from './styles';

export default function ModalScanQrCode() {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [scanUrl, setScanUrl] = useState('');
  const [patientName, setPatientName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { listen, publish } = useEventBus();
  const { config } = useConfig();
  const oidc = useOidcWithAdmin();

  const contactApi = useMemo(() => ContactApi.getInstance(), [ContactApi]);
  const formsService = useMemo(() => FormsService.getInstance(config, oidc.oidcService), [config, oidc.oidcService]);

  const initiateScanQrCode = useCallback(
    async (patientId?: number, patientData?: PatientDto) => {
      try {
        setIsLoading(true);
        setUrl('');
        setPatientName('');
        setScanUrl(await formsService.goToScanUrl());
        const patient = patientData || (await contactApi.getContactDetailsById(patientId!));

        checkPatientData(patient, publish, 'or');

        const { token } = await formsService.invite(patientId, PatientInviteMethod.QrCode);
        if (!token) {
          throw new Error('Failed to generate QR code');
        }

        setPatientName(`${patient.firstName} ${patient.lastName}`);
        setUrl(token);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        throw error;
      }
    },
    [contactApi, publish, formsService]
  );

  useEffect(() => {
    return listen('formsDialog', async (event: any) => {
      const data = event.data as { type: string; patientId?: number; patient?: PatientDto };

      if (data.type === 'scan-qr-code' && (data.patientId || data.patient)) {
        setOpen(true);
        try {
          await initiateScanQrCode(data.patientId, data.patient);
        } catch (error) {
          console.error(error);
          setOpen(false);
        }
      }
    });
  }, [listen, initiateScanQrCode]);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        Scan to Sign In {patientName}
        <IconButton sx={{ ml: 'auto' }} onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      {isLoading ? (
        <DialogContent sx={{ minHeight: '342px' }}>
          <CenterContent sx={{ minHeight: '200px' }}>
            <CircularProgress />
          </CenterContent>
        </DialogContent>
      ) : (
        <DialogContent sx={{ minHeight: '342px' }}>
          Please go to{' '}
          <a href={scanUrl} target="_blank" rel="noreferrer">
            {scanUrl}
          </a>{' '}
          on your tablet and scan the QR code to sign into the patient's forms.
          <br />
          <CenterContent sx={{ minHeight: '200px' }}>
            <QRCodeSVG value={url} width="40%" height="40%" />
          </CenterContent>
        </DialogContent>
      )}
    </Dialog>
  );
}
